import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, FileText, AlertCircle, Plus, X, Trash2, MapPin, Link as LinkIcon, Pencil, Search, Grid3X3, List, CalendarPlus, ExternalLink } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { trackFeatureUsage } from '../utils/analytics';
import { formatTime } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EVENT_TYPES = [
  { value: 'phone_call', label: 'Phone Call', color: 'bg-[#FAA519] text-black' },
  { value: 'meeting', label: 'Meeting', color: 'bg-[#80298F] text-white' },
  { value: 'event_hearing', label: 'Event/Hearing', color: 'bg-[#DC3239] text-white' },
  { value: 'medical', label: 'Medical', color: 'bg-blue-500 text-white' },
  { value: 'deadline', label: 'Deadline', color: 'bg-red-600 text-white' },
  { value: 'other', label: 'Other', color: 'bg-gray-500 text-white' }
];

function Calendar({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseFromUrl = searchParams.get('case');
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(caseFromUrl || 'all');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Modal state for creating/editing events
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [createForm, setCreateForm] = useState({
    case_id: '',
    title: '',
    appointment_type: 'meeting',
    date: '',
    time: '09:00',
    location: '',
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    title: '',
    appointment_type: 'meeting',
    date: '',
    time: '09:00',
    location: '',
    notes: ''
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Search and view state
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  
  // Add to Calendar state
  const [calendarLinks, setCalendarLinks] = useState({});
  const [loadingLinks, setLoadingLinks] = useState({});

  const isStaffOrAdmin = user?.role === 'staff' || user?.role === 'admin';

  // Fetch calendar links for an appointment
  const fetchCalendarLinks = async (appointmentId) => {
    if (calendarLinks[appointmentId] || loadingLinks[appointmentId]) return;
    
    setLoadingLinks(prev => ({ ...prev, [appointmentId]: true }));
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}/calendar-links`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarLinks(prev => ({ ...prev, [appointmentId]: data }));
      }
    } catch (error) {
      console.error('Failed to fetch calendar links:', error);
    } finally {
      setLoadingLinks(prev => ({ ...prev, [appointmentId]: false }));
    }
  };

  // Open calendar link in new tab
  const openCalendarLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Download ICS file
  const downloadICS = async (appointmentId, title) => {
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}/download.ics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.replace(/\s+/g, '_')}.ics`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to download ICS:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // Track calendar page visit
    trackFeatureUsage('Calendar');
  }, []);

  // Update selectedCase when URL param changes
  useEffect(() => {
    if (caseFromUrl && cases.length > 0) {
      const caseExists = cases.some(c => c.case_id === caseFromUrl);
      if (caseExists) {
        setSelectedCase(caseFromUrl);
      }
    }
  }, [caseFromUrl, cases]);

  const fetchData = async () => {
    try {
      const [appointmentsRes, tasksRes, casesRes] = await Promise.all([
        fetch(`${API}/appointments`, { credentials: 'include' }),
        fetch(`${API}/tasks`, { credentials: 'include' }),
        fetch(`${API}/cases`, { credentials: 'include' })
      ]);

      if (appointmentsRes.ok) setAppointments(await appointmentsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (casesRes.ok) setCases(await casesRes.json());
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  // Helper to parse date strings consistently - handles both with and without "Z" suffix
  const parseAppointmentDate = (dateStr) => {
    if (!dateStr) return new Date();
    const str = String(dateStr);
    if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) {
      return new Date(str + 'Z');
    }
    return new Date(str);
  };

  // Format time for display - dates in MongoDB are UTC; convert to Pacific for display
  const formatAppointmentTime = (dateStr) => {
    if (!dateStr) return '';
    
    // Ensure naive datetime strings (no timezone info) are treated as UTC
    // MongoDB stores in UTC but Motor returns naive datetimes, Pydantic serializes without tz suffix
    const str = String(dateStr);
    let dateInput = str;
    if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) {
      dateInput = str + 'Z';
    }
    
    const date = new Date(dateInput);
    
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getAppointmentTypeColor = (type) => {
    const eventType = EVENT_TYPES.find(t => t.value === type);
    return eventType ? eventType.color : 'bg-gray-500 text-white';
  };

  const getAppointmentTypeLabel = (type) => {
    const eventType = EVENT_TYPES.find(t => t.value === type);
    return eventType ? eventType.label : type;
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem ? caseItem.case_name : 'Unknown Case';
  };

  // Filter appointments and tasks based on search query and selected case
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;
    
    // Filter by selected case
    if (selectedCase !== 'all') {
      filtered = filtered.filter(apt => apt.case_id === selectedCase);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(apt => {
        const caseName = getCaseName(apt.case_id).toLowerCase();
        const title = (apt.title || '').toLowerCase();
        const location = (apt.location || '').toLowerCase();
        const notes = (apt.notes || '').toLowerCase();
        const type = getAppointmentTypeLabel(apt.appointment_type).toLowerCase();
        return title.includes(query) || 
               caseName.includes(query) || 
               location.includes(query) || 
               notes.includes(query) ||
               type.includes(query);
      });
    }
    
    return filtered;
  }, [appointments, selectedCase, searchQuery, cases]);

  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(t => !t.completed);
    
    // Filter by selected case
    if (selectedCase !== 'all') {
      filtered = filtered.filter(task => task.case_id === selectedCase);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => {
        const title = (task.title || '').toLowerCase();
        const description = (task.description || '').toLowerCase();
        const assignee = (task.assignee_name || '').toLowerCase();
        return title.includes(query) || 
               description.includes(query) || 
               assignee.includes(query);
      });
    }
    
    return filtered;
  }, [tasks, selectedCase, searchQuery]);

  // Get all events sorted by date for list view
  const allEventsSorted = useMemo(() => {
    const events = [];
    
    // Add appointments
    filteredAppointments.forEach(apt => {
      events.push({
        type: 'appointment',
        date: parseAppointmentDate(apt.date),
        data: apt
      });
    });
    
    // Add tasks
    filteredTasks.forEach(task => {
      events.push({
        type: 'task',
        date: parseAppointmentDate(task.due_date),
        data: task
      });
    });
    
    // Sort by date
    events.sort((a, b) => a.date - b.date);
    
    return events;
  }, [filteredAppointments, filteredTasks]);

  // Group events by date for list view
  const eventsByDate = useMemo(() => {
    const groups = {};
    allEventsSorted.forEach(event => {
      const dateKey = event.date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = { date: event.date, appointments: [], tasks: [] };
      }
      if (event.type === 'appointment') {
        groups[dateKey].appointments.push(event.data);
      } else {
        groups[dateKey].tasks.push(event.data);
      }
    });
    return Object.values(groups);
  }, [allEventsSorted]);

  // Get events for a specific date (must be after filteredAppointments and filteredTasks)
  const getEventsForDate = React.useCallback((date) => {
    if (!date) return { appointments: [], tasks: [] };
    
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth();
    const targetDay = date.getDate();
    
    const dayAppointments = filteredAppointments.filter(apt => {
      const aptDate = parseAppointmentDate(apt.date);
      return aptDate.getFullYear() === targetYear &&
             aptDate.getMonth() === targetMonth &&
             aptDate.getDate() === targetDay;
    });
    
    const dayTasks = filteredTasks.filter(task => {
      const taskDate = parseAppointmentDate(task.due_date);
      return taskDate.getFullYear() === targetYear &&
             taskDate.getMonth() === targetMonth &&
             taskDate.getDate() === targetDay;
    });
    
    return { appointments: dayAppointments, tasks: dayTasks };
  }, [filteredAppointments, filteredTasks]);

  // Auto-switch to list view when searching
  React.useEffect(() => {
    if (searchQuery.trim() && (filteredAppointments.length > 0 || filteredTasks.length > 0)) {
      setViewMode('list');
    }
  }, [searchQuery, filteredAppointments, filteredTasks]);

  const openCreateModal = (preSelectedDate = null) => {
    const dateToUse = preSelectedDate || selectedDate || new Date();
    const dateStr = dateToUse.toISOString().split('T')[0];
    
    // Pre-select case from URL or use first case
    const defaultCaseId = (caseFromUrl && cases.some(c => c.case_id === caseFromUrl)) 
      ? caseFromUrl 
      : (cases.length > 0 ? cases[0].case_id : '');
    
    setCreateForm({
      case_id: defaultCaseId,
      title: '',
      appointment_type: 'meeting',
      date: dateStr,
      time: '09:00',
      location: '',
      notes: ''
    });
    setShowCreateModal(true);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!createForm.case_id || !createForm.title || !createForm.date || !createForm.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      // Combine date and time - treat input as Pacific Time
      // Create date string with explicit PST/PDT timezone
      const dateTimeStr = `${createForm.date}T${createForm.time}:00`;
      
      // Create a date object and explicitly set it as Pacific time
      // Then convert to ISO string for the API
      const pacificDate = new Date(dateTimeStr);
      
      // Get the Pacific timezone offset for this date
      const pacificOptions = { timeZone: 'America/Los_Angeles', timeZoneName: 'short' };
      const pacificStr = pacificDate.toLocaleString('en-US', pacificOptions);
      
      // For the API, we'll send the ISO string but the backend should treat it as Pacific time
      // The key is consistency - if the user enters 4:00 PM, we want to store 4:00 PM Pacific
      
      const response = await fetch(`${API}/appointments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: createForm.case_id,
          title: createForm.title,
          appointment_type: createForm.appointment_type,
          date: dateTimeStr,  // Send as local time string without timezone conversion
          location: createForm.location || null,
          notes: createForm.notes || null,
          attendees: []
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create event');
      }

      toast.success('Calendar event created successfully');
      setShowCreateModal(false);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete event');
      }

      toast.success('Event deleted successfully');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditModal = (appointment) => {
    const aptDate = parseAppointmentDate(appointment.date);
    
    // Convert to Pacific time for edit form display
    // Use toLocaleString with Pacific timezone to get the correct local date/time
    const pacificOptions = { timeZone: 'America/Los_Angeles' };
    const pacificDateStr = aptDate.toLocaleDateString('en-CA', pacificOptions); // YYYY-MM-DD format
    const pacificTimeStr = aptDate.toLocaleTimeString('en-GB', { 
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }); // HH:MM format
    
    setEditingAppointment(appointment);
    setEditForm({
      title: appointment.title,
      appointment_type: appointment.appointment_type,
      date: pacificDateStr,
      time: pacificTimeStr,
      location: appointment.location || '',
      notes: appointment.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    
    if (!editForm.title || !editForm.date || !editForm.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUpdating(true);
    try {
      // Combine date and time - send as local time string without timezone conversion
      const dateTimeStr = `${editForm.date}T${editForm.time}:00`;
      
      // Build query params for PATCH endpoint
      const params = new URLSearchParams();
      params.append('title', editForm.title);
      params.append('appointment_type', editForm.appointment_type);
      params.append('date', dateTimeStr);  // Send as local time string
      if (editForm.location) params.append('location', editForm.location);
      if (editForm.notes) params.append('notes', editForm.notes);
      
      const response = await fetch(`${API}/appointments/${editingAppointment.appointment_id}?${params.toString()}`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update event');
      }

      toast.success('Calendar event updated successfully');
      setShowEditModal(false);
      setEditingAppointment(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="calendar-heading">Calendar</h1>
            <p className="text-gray-600 mt-1">View and manage your appointments and deadlines</p>
          </div>
          
          {isStaffOrAdmin && (
            <Button 
              onClick={() => openCreateModal()} 
              className="bg-[#80298F] hover:bg-[#6b2278]"
              data-testid="create-event-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Event
            </Button>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Search Bar */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search events by title, case, location, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
              data-testid="calendar-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Navigation and Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={previousMonth} data-testid="prev-month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={today} data-testid="today-button">
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={nextMonth} data-testid="next-month">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold ml-4">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* View Toggle */}
              <div className="flex items-center border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                    viewMode === 'calendar'
                      ? 'bg-[#80298F] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="view-calendar"
                  title="Calendar View"
                >
                  <Grid3X3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                    viewMode === 'list'
                      ? 'bg-[#80298F] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="view-list"
                  title="List View"
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
              
              {/* Case Filter */}
              <div className="flex-1 sm:w-48">
                <Select value={selectedCase} onValueChange={setSelectedCase}>
                  <SelectTrigger data-testid="case-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cases</SelectItem>
                    {cases.map(c => (
                      <SelectItem key={c.case_id} value={c.case_id}>
                        {c.case_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-4 p-3 bg-[#80298F]/10 border border-[#80298F]/20 rounded-lg flex items-center justify-between">
            <p className="text-sm text-[#80298F]">
              Found {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''} and {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-[#80298F] hover:text-[#6b2278] text-sm font-medium"
            >
              Clear
            </button>
          </div>
        )}

        {viewMode === 'calendar' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" key={`calendar-${searchQuery}`}>
            {/* Calendar Grid */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS.map(day => (
                      <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((date, index) => {
                      if (!date) {
                        return <div key={index} className="aspect-square"></div>;
                      }

                      const { appointments: dayAppointments, tasks: dayTasks } = getEventsForDate(date);
                      const hasEvents = dayAppointments.length > 0 || dayTasks.length > 0;

                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedDate(date)}
                          onDoubleClick={() => isStaffOrAdmin && openCreateModal(date)}
                          className={`aspect-square p-1 rounded-lg border transition-all hover:border-[#80298F] ${
                            isToday(date)
                              ? 'bg-[#80298F] text-white border-[#80298F]'
                              : isPast(date)
                              ? 'bg-gray-50 text-gray-400 border-gray-200'
                              : 'bg-white border-gray-200'
                          } ${
                            selectedDate?.toDateString() === date.toDateString()
                              ? 'ring-2 ring-[#80298F]'
                              : ''
                          }`}
                          data-testid={`date-${date.getDate()}`}
                          title={isStaffOrAdmin ? 'Double-click to create event' : ''}
                        >
                          <div className="text-sm font-semibold">{date.getDate()}</div>
                          {hasEvents && (
                            <div className="flex flex-col gap-0.5 mt-1">
                              {dayAppointments.length > 0 && (
                                <div className={`w-full h-1 rounded ${isToday(date) ? 'bg-white' : 'bg-[#FAA519]'}`}></div>
                              )}
                              {dayTasks.length > 0 && (
                                <div className={`w-full h-1 rounded ${isToday(date) ? 'bg-white' : 'bg-[#DC3239]'}`}></div>
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-xs">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded bg-[#FAA519]"></div>
                      <span>Appointments</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded bg-[#DC3239]"></div>
                      <span>Tasks</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded bg-[#80298F]"></div>
                      <span>Today</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Events List for Selected Day */}
            <div>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle>
                    {selectedDate 
                      ? `Events for ${MONTHS[selectedDate.getMonth()]} ${selectedDate.getDate()}`
                      : 'Select a date'
                    }
                  </CardTitle>
                  {isStaffOrAdmin && selectedDate && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openCreateModal(selectedDate)}
                      className="text-[#80298F] hover:text-[#6b2278]"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {selectedDate ? (
                    <div className="space-y-4">
                      {(() => {
                        const { appointments: dayAppointments, tasks: dayTasks } = getEventsForDate(selectedDate);
                        
                        if (dayAppointments.length === 0 && dayTasks.length === 0) {
                          return (
                            <div className="text-center py-8 text-gray-500">
                              <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                              <p>No events for this day</p>
                              {isStaffOrAdmin && (
                                <Button 
                                  variant="link" 
                                  onClick={() => openCreateModal(selectedDate)}
                                  className="text-[#80298F] mt-2"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add Event
                                </Button>
                              )}
                            </div>
                          );
                        }

                        return (
                          <>
                            {/* Appointments */}
                            {dayAppointments.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                  <CalendarIcon className="h-4 w-4 mr-2" />
                                  Appointments ({dayAppointments.length})
                                </h4>
                                <div className="space-y-2">
                                  {dayAppointments.map(apt => (
                                    <div key={apt.appointment_id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="font-medium text-sm text-gray-900">{apt.title}</p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            <Clock className="h-3 w-3 inline mr-1" />
                                            {formatAppointmentTime(apt.date)}
                                          </p>
                                          {apt.location && (
                                            <p className="text-xs text-gray-500 mt-1">
                                              {apt.location.startsWith('http') ? (
                                                <a 
                                                  href={apt.location} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="text-[#80298F] hover:underline flex items-center"
                                                >
                                                  <LinkIcon className="h-3 w-3 inline mr-1" />
                                                  Meeting Link
                                                </a>
                                              ) : (
                                                <>
                                                  <MapPin className="h-3 w-3 inline mr-1" />
                                                  {apt.location}
                                                </>
                                              )}
                                            </p>
                                          )}
                                          <p className="text-xs text-gray-400 mt-1">
                                            Case: {getCaseName(apt.case_id)}
                                          </p>
                                          {apt.notes && (
                                            <p className="text-xs text-gray-600 mt-1">{apt.notes}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className={`text-xs px-2 py-1 rounded ${getAppointmentTypeColor(apt.appointment_type)}`}>
                                            {getAppointmentTypeLabel(apt.appointment_type)}
                                          </span>
                                          {/* Add to Calendar Dropdown */}
                                          <DropdownMenu onOpenChange={(open) => open && fetchCalendarLinks(apt.appointment_id)}>
                                            <DropdownMenuTrigger asChild>
                                              <button 
                                                className="text-gray-400 hover:text-[#80298F] transition-colors"
                                                title="Add to Calendar"
                                                data-testid={`add-to-calendar-${apt.appointment_id}`}
                                              >
                                                <CalendarPlus className="h-4 w-4" />
                                              </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                              {loadingLinks[apt.appointment_id] ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                                              ) : calendarLinks[apt.appointment_id] ? (
                                                <>
                                                  <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].google_calendar)}>
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Google Calendar
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].outlook)}>
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Outlook.com
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].yahoo)}>
                                                    <ExternalLink className="h-4 w-4 mr-2" />
                                                    Yahoo Calendar
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem onClick={() => downloadICS(apt.appointment_id, apt.title)}>
                                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                                    Download .ics
                                                  </DropdownMenuItem>
                                                </>
                                              ) : (
                                                <div className="px-3 py-2 text-sm text-gray-500">Click to load options</div>
                                              )}
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                          {isStaffOrAdmin && (
                                            <>
                                              <button 
                                                onClick={() => openEditModal(apt)}
                                                className="text-gray-400 hover:text-[#80298F] transition-colors"
                                                title="Edit event"
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </button>
                                              <button 
                                                onClick={() => handleDeleteEvent(apt.appointment_id)}
                                                className="text-gray-400 hover:text-red-500 transition-colors"
                                                title="Delete event"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Tasks */}
                            {dayTasks.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                  <FileText className="h-4 w-4 mr-2" />
                                  Tasks Due ({dayTasks.length})
                                </h4>
                                <div className="space-y-2">
                                  {dayTasks.map(task => (
                                    <div key={task.task_id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="font-medium text-sm text-gray-900">{task.title}</p>
                                          {task.description && (
                                            <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                                          )}
                                          <p className="text-xs text-gray-500 mt-1">
                                            Assigned to: {task.assignee_name}
                                          </p>
                                        </div>
                                        {task.priority === 'urgent' && (
                                          <AlertCircle className="h-4 w-4 text-[#DC3239]" />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Click on a date to view events</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Events Summary */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm">Upcoming This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Appointments</span>
                      <span className="font-semibold">{filteredAppointments.filter(a => {
                        const aptDate = new Date(a.date);
                        return aptDate.getMonth() === currentDate.getMonth() && 
                               aptDate.getFullYear() === currentDate.getFullYear();
                      }).length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Pending Tasks</span>
                      <span className="font-semibold">{filteredTasks.filter(t => {
                        const taskDate = new Date(t.due_date);
                        return taskDate.getMonth() === currentDate.getMonth() && 
                               taskDate.getFullYear() === currentDate.getFullYear();
                      }).length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* List View */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <List className="h-5 w-5" />
                All Events
                <span className="text-sm font-normal text-gray-500">
                  ({filteredAppointments.length} appointments, {filteredTasks.length} tasks)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {eventsByDate.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-lg font-medium">No events found</p>
                  <p className="text-sm mt-1">
                    {searchQuery ? 'Try adjusting your search terms' : 'No events scheduled'}
                  </p>
                  {isStaffOrAdmin && (
                    <Button 
                      onClick={() => openCreateModal()}
                      className="mt-4 bg-[#80298F] hover:bg-[#6b2278]"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Event
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {eventsByDate.map((group, groupIndex) => (
                    <div key={groupIndex} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
                      {/* Date Header */}
                      <div className={`flex items-center gap-3 mb-4 pb-2 border-b ${
                        isToday(group.date) ? 'border-[#80298F]' : 'border-gray-200'
                      }`}>
                        <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg ${
                          isToday(group.date) 
                            ? 'bg-[#80298F] text-white' 
                            : isPast(group.date) 
                            ? 'bg-gray-100 text-gray-500' 
                            : 'bg-gray-50 text-gray-900'
                        }`}>
                          <span className="text-xs uppercase">{DAYS[group.date.getDay()]}</span>
                          <span className="text-xl font-bold">{group.date.getDate()}</span>
                        </div>
                        <div>
                          <p className={`font-semibold ${isToday(group.date) ? 'text-[#80298F]' : 'text-gray-900'}`}>
                            {MONTHS[group.date.getMonth()]} {group.date.getDate()}, {group.date.getFullYear()}
                          </p>
                          <p className="text-sm text-gray-500">
                            {group.appointments.length} appointment{group.appointments.length !== 1 ? 's' : ''}, {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        {isToday(group.date) && (
                          <span className="ml-auto px-2 py-1 text-xs font-medium bg-[#80298F] text-white rounded">
                            Today
                          </span>
                        )}
                      </div>

                      {/* Events for this date */}
                      <div className="space-y-3 ml-0 sm:ml-[74px]">
                        {/* Appointments */}
                        {group.appointments.map(apt => (
                          <div key={apt.appointment_id} className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#80298F]/30 transition-colors">
                            <div className={`flex-shrink-0 w-1 h-full min-h-[60px] rounded-full ${getAppointmentTypeColor(apt.appointment_type).split(' ')[0]}`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{apt.title}</p>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3.5 w-3.5" />
                                      {formatAppointmentTime(apt.date)}
                                    </span>
                                    {apt.location && (
                                      <span className="flex items-center gap-1">
                                        {apt.location.startsWith('http') ? (
                                          <a 
                                            href={apt.location} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-[#80298F] hover:underline flex items-center gap-1"
                                          >
                                            <LinkIcon className="h-3.5 w-3.5" />
                                            Meeting Link
                                          </a>
                                        ) : (
                                          <>
                                            <MapPin className="h-3.5 w-3.5" />
                                            <span className="truncate max-w-[150px]">{apt.location}</span>
                                          </>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Case: {getCaseName(apt.case_id)}
                                  </p>
                                  {apt.notes && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{apt.notes}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getAppointmentTypeColor(apt.appointment_type)}`}>
                                    {getAppointmentTypeLabel(apt.appointment_type)}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {/* Add to Calendar Dropdown */}
                                    <DropdownMenu onOpenChange={(open) => open && fetchCalendarLinks(apt.appointment_id)}>
                                      <DropdownMenuTrigger asChild>
                                        <button 
                                          className="p-1 text-gray-400 hover:text-[#80298F] transition-colors"
                                          title="Add to Calendar"
                                          data-testid={`add-to-calendar-list-${apt.appointment_id}`}
                                        >
                                          <CalendarPlus className="h-4 w-4" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-48">
                                        {loadingLinks[apt.appointment_id] ? (
                                          <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                                        ) : calendarLinks[apt.appointment_id] ? (
                                          <>
                                            <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].google_calendar)}>
                                              <ExternalLink className="h-4 w-4 mr-2" />
                                              Google Calendar
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].outlook)}>
                                              <ExternalLink className="h-4 w-4 mr-2" />
                                              Outlook.com
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => openCalendarLink(calendarLinks[apt.appointment_id].yahoo)}>
                                              <ExternalLink className="h-4 w-4 mr-2" />
                                              Yahoo Calendar
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => downloadICS(apt.appointment_id, apt.title)}>
                                              <CalendarIcon className="h-4 w-4 mr-2" />
                                              Download .ics
                                            </DropdownMenuItem>
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-sm text-gray-500">Click to load options</div>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                    {isStaffOrAdmin && (
                                      <>
                                        <button 
                                          onClick={() => openEditModal(apt)}
                                          className="p-1 text-gray-400 hover:text-[#80298F] transition-colors"
                                          title="Edit event"
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </button>
                                        <button 
                                          onClick={() => handleDeleteEvent(apt.appointment_id)}
                                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                          title="Delete event"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Tasks */}
                        {group.tasks.map(task => (
                          <div key={task.task_id} className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#DC3239]/30 transition-colors">
                            <div className="flex-shrink-0 w-1 h-full min-h-[60px] rounded-full bg-[#DC3239]"></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{task.title}</p>
                                  {task.description && (
                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    Assigned to: {task.assignee_name}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs px-2 py-1 rounded bg-[#DC3239]/10 text-[#DC3239] whitespace-nowrap">
                                    Task Due
                                  </span>
                                  {task.priority === 'urgent' && (
                                    <AlertCircle className="h-4 w-4 text-[#DC3239]" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Create Calendar Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="case">Case *</Label>
              <Select 
                value={createForm.case_id} 
                onValueChange={(value) => setCreateForm({...createForm, case_id: value})}
              >
                <SelectTrigger data-testid="create-case-select">
                  <SelectValue placeholder="Select a case" />
                </SelectTrigger>
                <SelectContent>
                  {cases.map(c => (
                    <SelectItem key={c.case_id} value={c.case_id}>
                      {c.case_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={createForm.title}
                onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                placeholder="e.g., Client Meeting"
                required
                data-testid="create-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_type">Event Type *</Label>
              <Select 
                value={createForm.appointment_type} 
                onValueChange={(value) => setCreateForm({...createForm, appointment_type: value})}
              >
                <SelectTrigger data-testid="create-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${type.color.split(' ')[0]}`}></span>
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={createForm.date}
                  onChange={(e) => setCreateForm({...createForm, date: e.target.value})}
                  required
                  data-testid="create-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  type="time"
                  value={createForm.time}
                  onChange={(e) => setCreateForm({...createForm, time: e.target.value})}
                  required
                  data-testid="create-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location / Meeting Link (Optional)</Label>
              <Input
                id="location"
                value={createForm.location}
                onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                placeholder="e.g., Conference Room A or https://zoom.us/j/..."
                data-testid="create-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={createForm.notes}
                onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                placeholder="Add any additional notes..."
                rows={3}
                data-testid="create-notes-input"
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={creating}
                className="bg-[#80298F] hover:bg-[#6b2278]"
                data-testid="create-event-submit"
              >
                {creating ? 'Creating...' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Edit Calendar Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Event Title *</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                placeholder="e.g., Client Meeting"
                required
                data-testid="edit-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-event_type">Event Type *</Label>
              <Select 
                value={editForm.appointment_type} 
                onValueChange={(value) => setEditForm({...editForm, appointment_type: value})}
              >
                <SelectTrigger data-testid="edit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${type.color.split(' ')[0]}`}></span>
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                  required
                  data-testid="edit-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time">Time *</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={editForm.time}
                  onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                  required
                  data-testid="edit-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location / Meeting Link (Optional)</Label>
              <Input
                id="edit-location"
                value={editForm.location}
                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                placeholder="e.g., Conference Room A or https://zoom.us/j/..."
                data-testid="edit-location-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes (Optional)</Label>
              <Textarea
                id="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Add any additional notes..."
                rows={3}
                data-testid="edit-notes-input"
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAppointment(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={updating}
                className="bg-[#80298F] hover:bg-[#6b2278]"
                data-testid="edit-event-submit"
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Calendar;
